<template>
  <v-card
    variant="flat"
    class="mb-4 pa-4"
  >
    <h3 class="text-h6 mb-4">
      Legal Agreements
    </h3>
    <v-form ref="form">
      <v-row>
        <v-col cols="12">
          <!-- Signing Authority Section -->
          <v-card
            variant="outlined"
            class="mb-6"
          >
            <v-card-title class="text-subtitle-1 py-4">
              Signing Authority
            </v-card-title>
            <v-card-text>
              <p class="text-body-2 text-medium-emphasis mb-4">
                We need to send our Non-Disclosure Agreement (NDA) and Evaluation Software License Agreement (ESLA) 
                for electronic signature via Adobe Acrobat Sign.
              </p>

              <v-radio-group
                v-model="modelValue.hasSigningAuthority"
                class="mb-4"
              >
                <v-radio
                  value="yes"
                  label="I have authority to sign legal agreements on behalf of my organization"
                />
                <v-radio
                  value="no"
                  label="Someone else needs to sign these agreements"
                />
              </v-radio-group>

              <!-- Authorized Signer Information -->
              <v-expand-transition>
                <div v-if="modelValue.hasSigningAuthority === 'no'">
                  <v-divider class="mb-4" />
                  <p class="text-body-2 font-weight-medium mb-4">
                    Please provide the contact information for the authorized signer:
                  </p>
                  <v-row>
                    <v-col
                      cols="12"
                      md="6"
                    >
                      <v-text-field
                        v-model="modelValue.authorizedSigner.name"
                        label="Full Name*"
                        variant="outlined"
                        :rules="[v => !!v || 'Name is required']"
                        required
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      md="6"
                    >
                      <v-text-field
                        v-model="modelValue.authorizedSigner.email"
                        label="Email Address*"
                        variant="outlined"
                        :rules="[
                          v => !!v || 'Email is required',
                          v => /.+@.+\..+/.test(v) || 'Email must be valid'
                        ]"
                        required
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="modelValue.authorizedSigner.title"
                        label="Job Title*"
                        variant="outlined"
                        :rules="[v => !!v || 'Job title is required']"
                        required
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>
            </v-card-text>
          </v-card>

          <!-- Agreement Summary Cards -->
          <div class="d-flex gap-4">
            <v-card
              variant="outlined"
              class="flex-1"
            >
              <v-card-title class="text-subtitle-1 py-4">
                <v-icon
                  icon="mdi-file-document-outline"
                  class="me-2"
                  color="primary"
                />
                Non-Disclosure Agreement
              </v-card-title>
              <v-card-text class="pb-4">
                <p class="text-body-2 text-medium-emphasis">
                  Protects confidential information shared between Peraso and your organization.
                </p>
              </v-card-text>
            </v-card>

            <v-card
              variant="outlined"
              class="flex-1"
            >
              <v-card-title class="text-subtitle-1 py-4">
                <v-icon
                  icon="mdi-file-certificate-outline"
                  class="me-2"
                  color="primary"
                />
                Evaluation Software License Agreement
              </v-card-title>
              <v-card-text class="pb-4">
                <p class="text-body-2 text-medium-emphasis">
                  Defines the terms and conditions for evaluating and using Peraso software and development tools.
                </p>
              </v-card-text>
            </v-card>
          </div>

          <!-- Consent Checkbox -->
          <v-checkbox
            v-model="modelValue.consent"
            class="mt-6"
            label="I understand that these agreements will be sent via Adobe Acrobat Sign for electronic signature"
            :rules="[v => !!v || 'You must acknowledge this']"
            required
          />
        </v-col>
      </v-row>
    </v-form>
  </v-card>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { VForm } from 'vuetify/components'

export interface IAuthorizedSigner {
  name: string
  email: string
  title: string
}

export interface ILegalData {
  hasSigningAuthority: 'yes' | 'no' | null
  authorizedSigner: IAuthorizedSigner
  consent: boolean
}

const modelValue = defineModel<ILegalData>('modelValue', { required: true })

const form = ref<VForm | null>(null)

defineExpose({ form })
</script>

<style scoped>
.flex-1 {
  flex: 1;
}
</style> 